import { cx } from '../lib/cx'
import { SEVERIDAD_RELLENO, type Severidad } from './tokens'

/**
 * Barra de proporción de una sola serie: activación de una cuenta, paso de una
 * etapa del funnel, cobertura de una carga.
 *
 * El relleno lleva un anillo interno neutro por la misma razón que el punto del
 * semáforo: los rellenos de marca no llegan a 3:1 sobre el track gris, así que
 * el contorno es lo que hace visible dónde termina la barra.
 *
 * `label` es obligatorio: la barra es un `progressbar` con nombre accesible y su
 * valor viaja en `aria-valuenow`, no solo en el ancho del div.
 */
export interface ProgressBarProps {
  /** 0–100. Se recorta al rango; un 140% dibujado lleno miente menos que roto. */
  pct: number
  /** Qué representa la barra, ej. "Activación de Armytech". */
  label: string
  severidad?: Severidad
  /** Texto a la derecha de la barra. `null` = sin texto. */
  etiqueta?: string | null
  size?: 'sm' | 'md'
  className?: string
}

export function ProgressBar({
  pct,
  label,
  severidad = 'unknown',
  etiqueta,
  size = 'md',
  className,
}: ProgressBarProps) {
  const valor = Math.max(0, Math.min(100, pct))

  return (
    <div
      data-testid="progress-bar"
      className={cx('flex min-w-0 items-center gap-2', className)}
    >
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(valor)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${Math.round(valor)}%`}
        className={cx(
          'min-w-[56px] flex-1 overflow-hidden rounded-full bg-n100',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        <span
          data-testid="progress-bar-fill"
          style={{ width: `${valor}%` }}
          className={cx(
            'block h-full rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,.28)] transition-[width] duration-300 ease-brand',
            SEVERIDAD_RELLENO[severidad],
          )}
        />
      </div>
      {etiqueta !== null && etiqueta !== undefined ? (
        <span className="tabular w-12 shrink-0 text-right text-meta text-muted-strong">
          {etiqueta}
        </span>
      ) : null}
    </div>
  )
}
